import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { boardAPI, deptAPI, videoAPI } from "../lib/api";
import { useWebSocket } from "../hooks/useWebSocket";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Switch } from "../components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import DecisionPanel from "../components/DecisionPanel";
import InviteModal from "../components/InviteModal";
import TutorialVideos from "../components/TutorialVideos";
import ActivityFeed from "../components/ActivityFeed";
import PresenceBar from "../components/PresenceBar";
import VotingPanel from "../components/VotingPanel";
import VoteRevealView from "../components/VoteRevealView";
import VotingSettings from "../components/VotingSettings";
import DepartmentAssignment from "../components/DepartmentAssignment";
import SetupWizard from "../components/SetupWizard";
import DisruptionSession from "../components/DisruptionSession";
import {
  ArrowLeft, BarChart3, Lock, Unlock, Timer, Megaphone, TrendingUp,
  Headphones, DollarSign, Users, Settings, Monitor, Lightbulb, Shield,
  Compass, Zap, Brain, Download, ChevronRight, Star, UserPlus, BookOpen,
  Calendar, Clock, AlertTriangle, Activity, Vote, Eye, Skull, Rocket,
  CheckCircle2, Flag, Anchor, UserCheck, PenLine, Play, Plus as PlusIcon, Crosshair, Trash,
} from "lucide-react";

const DEPT_ICONS = {
  megaphone: Megaphone, "trending-up": TrendingUp, headphones: Headphones,
  "dollar-sign": DollarSign, users: Users, settings: Settings,
  monitor: Monitor, lightbulb: Lightbulb, shield: Shield, compass: Compass,
};

const DECISION_COLORS = {
  delete: { bg: "bg-red-500", border: "border-l-red-500", badge: "bg-red-50 text-red-700 border-red-200" },
  automate: { bg: "bg-amber-500", border: "border-l-amber-500", badge: "bg-amber-50 text-amber-700 border-amber-200" },
  augment: { bg: "bg-violet-500", border: "border-l-violet-500", badge: "bg-violet-50 text-violet-700 border-violet-200" },
  protect: { bg: "bg-emerald-500", border: "border-l-emerald-500", badge: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

const CountdownBanner = ({ scheduledEnd, durationMinutes, gameMode }) => {
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    const calcTimeLeft = () => {
      let target = null;
      if (scheduledEnd) {
        target = new Date(scheduledEnd).getTime();
      }
      if (!target) return null;

      const now = Date.now();
      const diff = target - now;
      if (diff <= 0) return { expired: true };

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diff / (1000 * 60)) % 60);
      const seconds = Math.floor((diff / 1000) % 60);
      return { days, hours, minutes, seconds, expired: false, total: diff };
    };

    setTimeLeft(calcTimeLeft());
    const interval = setInterval(() => setTimeLeft(calcTimeLeft()), 1000);
    return () => clearInterval(interval);
  }, [scheduledEnd]);

  if (!timeLeft) return null;

  if (timeLeft.expired) {
    return (
      <div className="bg-red-50 border-b border-red-200 px-4 py-2 flex items-center justify-center gap-2" data-testid="countdown-expired">
        <AlertTriangle className="w-4 h-4 text-red-500" />
        <span className="text-sm font-medium text-red-700">
          {gameMode === "live" ? "Session has ended" : "Deadline has passed"}
        </span>
      </div>
    );
  }

  const isUrgent = timeLeft.total < 1000 * 60 * 60; // < 1 hour
  const bgColor = isUrgent ? "bg-red-50 border-red-200" : "bg-amber-50 border-amber-200";
  const textColor = isUrgent ? "text-red-700" : "text-amber-700";
  const numColor = isUrgent ? "text-red-800" : "text-amber-800";

  return (
    <div className={`${bgColor} border-b px-4 py-2 flex items-center justify-center gap-4`} data-testid="countdown-banner">
      <Clock className={`w-4 h-4 ${textColor}`} />
      <span className={`text-sm font-medium ${textColor}`}>
        {gameMode === "live" ? "Session ends in:" : "Deadline:"}
      </span>
      <div className="flex items-center gap-2">
        {timeLeft.days > 0 && (
          <span className={`font-mono text-sm font-bold ${numColor}`}>{timeLeft.days}d</span>
        )}
        <span className={`font-mono text-sm font-bold ${numColor}`}>
          {String(timeLeft.hours).padStart(2, "0")}:{String(timeLeft.minutes).padStart(2, "0")}:{String(timeLeft.seconds).padStart(2, "0")}
        </span>
      </div>
      {durationMinutes && (
        <Badge variant="outline" className={`text-[10px] ${textColor} border-current`}>
          {durationMinutes}min per player
        </Badge>
      )}
    </div>
  );
};

export default function GameBoardPage() {
  const { boardId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedStall, setSelectedStall] = useState(null);
  const [selectedDeptName, setSelectedDeptName] = useState("");
  const [activeDeptIdx, setActiveDeptIdx] = useState(null);
  const [timerActive, setTimerActive] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(300);
  const [timerRunning, setTimerRunning] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [showTutorials, setShowTutorials] = useState(false);
  const [showActivity, setShowActivity] = useState(false);
  const [videosDismissed, setVideosDismissed] = useState(true);
  const [showVotingSettings, setShowVotingSettings] = useState(false);
  const [showVoteResults, setShowVoteResults] = useState(false);
  const [myVotes, setMyVotes] = useState([]);
  const [voteCounts, setVoteCounts] = useState({});
  const [activeTab, setActiveTab] = useState("optimization");
  const [showDeptAssign, setShowDeptAssign] = useState(false);
  const [addStallDept, setAddStallDept] = useState(null); // dept to add custom stall to
  const [newStall, setNewStall] = useState({ name: "", description: "" });
  const [spotlight, setSpotlight] = useState(null); // {target_type, department_idx, stall_id, department_name, stall_name}

  // WebSocket
  const { connected, onlineUsers, activities, sendViewing, sendTimerSync, sendSpotlight, sendSpotlightClear, registerHandlers } = useWebSocket(boardId, user);

  const fetchBoard = useCallback(async () => {
    try {
      const res = await boardAPI.get(boardId);
      setBoard(res.data);
    } catch {
      toast.error("Failed to load board");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  }, [boardId, navigate]);

  useEffect(() => { fetchBoard(); }, [fetchBoard]);

  // Register WS handlers for real-time sync
  useEffect(() => {
    registerHandlers({
      onDecisionUpdate: (msg) => {
        setBoard((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            departments: prev.departments.map((dept) => ({
              ...dept,
              stalls: dept.stalls.map((s) =>
                s.id === msg.stall_id
                  ? { ...s, decision: msg.decision, confidence: msg.confidence, decided_by: msg.user_id }
                  : s
              ),
            })),
          };
        });
        toast.info(`${msg.user_name} marked "${msg.stall_name}" as ${msg.decision ? msg.decision.charAt(0).toUpperCase() + msg.decision.slice(1) : "Undecided"}`, { duration: 3000 });
      },
      onLockUpdate: (msg) => {
        setBoard((prev) => prev ? { ...prev, is_locked: msg.is_locked } : prev);
        toast.info(`${msg.user_name} ${msg.is_locked ? "locked" : "unlocked"} the board`);
      },
      onTimerUpdate: (msg) => {
        if (msg.action === "started") {
          setTimerActive(true);
          setTimerSeconds(msg.seconds || 300);
          setTimerRunning(true);
          toast.info(`${msg.user_name} started the timer`);
        } else if (msg.action === "paused") {
          setTimerRunning(false);
        } else if (msg.action === "reset") {
          setTimerActive(false);
          setTimerRunning(false);
        }
      },
      onVoteCast: (msg) => {
        setVoteCounts((prev) => ({ ...prev, [msg.stall_id]: msg.vote_count }));
      },
      onVotesRevealed: () => {
        setBoard((prev) => prev ? { ...prev, votes_revealed: true } : prev);
        toast.info("Votes have been revealed!");
        fetchBoard();
      },
      onGameStatusChanged: (msg) => {
        setBoard((prev) => prev ? { ...prev, status: msg.status } : prev);
        if (msg.status === "completed") {
          toast.info(`${msg.user_name || "Facilitator"} ended Part 1 — moving to Part 2`);
          setActiveTab("disruption");
        } else {
          toast.info(`${msg.user_name || "Facilitator"} resumed Part 1`);
        }
      },
      onDisruptionPhaseChanged: (msg) => {
        toast.info(`${msg.user_name || "Facilitator"} moved to: ${msg.phase?.replace("phase1", "Phase 1: The Attack").replace("phase2", "Phase 2: Company 2.0").replace("brainstorm", "Brainstorm").replace("voting", "Dot Voting").replace("completed", "Results")}`);
      },
      onSpotlight: (msg) => {
        setSpotlight(msg);
        // Auto-scroll to spotlighted element
        if (msg.target_type === "department" && msg.department_idx !== undefined) {
          const el = document.getElementById(`dept-col-${msg.department_idx}`);
          el?.scrollIntoView({ behavior: "smooth", inline: "center", block: "start" });
        } else if (msg.target_type === "stall" && msg.stall_id) {
          const el = document.querySelector(`[data-testid*="stall-card"][data-testid*="${msg.stall_id?.slice(0, 8)}"]`) || document.getElementById(`dept-col-${msg.department_idx}`);
          el?.scrollIntoView({ behavior: "smooth", inline: "center", block: "center" });
        }
        toast.info(`${msg.user_name || "Facilitator"} spotlighted: ${msg.stall_name || msg.department_name}`);
      },
      onSpotlightClear: () => {
        setSpotlight(null);
      },
    });
  }, [registerHandlers, fetchBoard]);

  // Fetch votes when voting is enabled
  useEffect(() => {
    const fetchVotes = async () => {
      if (!board?.voting_enabled) return;
      try {
        const [myRes, countsRes] = await Promise.all([
          import("../lib/api").then(m => m.voteAPI.getMyVotes(boardId)),
          import("../lib/api").then(m => m.voteAPI.getCounts(boardId)),
        ]);
        setMyVotes(myRes.data.votes || []);
        setVoteCounts(countsRes.data.counts || {});
      } catch { /* silent */ }
    };
    fetchVotes();
  }, [board?.voting_enabled, boardId]);

  // Check if tutorials should auto-show
  useEffect(() => {
    const checkVideos = async () => {
      try {
        const res = await videoAPI.getProgress(boardId);
        const dismissed = res.data.dismissed;
        const watched = res.data.watched || [];
        setVideosDismissed(dismissed);
        if (!dismissed && watched.length === 0) {
          setShowTutorials(true);
        }
      } catch {
        // silent
      }
    };
    if (boardId) checkVideos();
  }, [boardId]);

  // Duration-based timer
  useEffect(() => {
    if (board?.duration_minutes && !timerActive) {
      setTimerSeconds(board.duration_minutes * 60);
    }
  }, [board?.duration_minutes, timerActive]);

  useEffect(() => {
    let interval;
    if (timerRunning && timerSeconds > 0) {
      interval = setInterval(() => setTimerSeconds((s) => s - 1), 1000);
    } else if (timerSeconds === 0 && timerRunning) {
      setTimerRunning(false);
      toast.info("Time's up!");
    }
    return () => clearInterval(interval);
  }, [timerRunning, timerSeconds]);

  const isOwner = board?.created_by === user?.id;

  const toggleGameStatus = async () => {
    const newStatus = board.status === "completed" ? "in_progress" : "completed";
    const msg = newStatus === "completed" ? "End the game and mark as complete?" : "Continue the game?";
    if (!window.confirm(msg)) return;
    try {
      await boardAPI.toggleStatus(boardId, newStatus);
      setBoard((prev) => ({ ...prev, status: newStatus }));
      toast.success(newStatus === "completed" ? "Game ended!" : "Game resumed!");
      // Auto-switch to Part 2 when ending
      if (newStatus === "completed") {
        setActiveTab("disruption");
      }
    } catch {
      toast.error("Failed to update status");
    }
  };

  const addCustomStall = async () => {
    if (!addStallDept || !newStall.name.trim()) return;
    try {
      const res = await boardAPI.addCustomStall(boardId, addStallDept, newStall);
      setBoard((prev) => ({
        ...prev,
        departments: prev.departments.map((d) =>
          d.id === addStallDept ? { ...d, stalls: [...d.stalls, res.data] } : d
        ),
      }));
      setAddStallDept(null);
      setNewStall({ name: "", description: "" });
      toast.success("Custom idea added!");
    } catch {
      toast.error("Failed to add");
    }
  };

  const toggleLock = async () => {
    try {
      await boardAPI.lock(boardId, !board.is_locked);
      setBoard((prev) => ({ ...prev, is_locked: !prev.is_locked }));
      toast.success(board.is_locked ? "Board unlocked" : "Board locked");
    } catch {
      toast.error("Failed to toggle lock");
    }
  };

  const toggleDept = async (deptId, currentVal) => {
    try {
      await deptAPI.toggle(boardId, deptId, !currentVal);
      setBoard((prev) => ({
        ...prev,
        departments: prev.departments.map((d) =>
          d.id === deptId ? { ...d, is_applicable: !d.is_applicable } : d
        ),
      }));
    } catch {
      toast.error("Failed to toggle department");
    }
  };

  const handleStallClick = (stall, deptName) => {
    if (board.voting_enabled && !board.votes_revealed) {
      setSelectedStall(stall);
      setSelectedDeptName(deptName);
    } else {
      setSelectedStall(stall);
      setSelectedDeptName(deptName);
    }
  };

  const handleVoted = (vote) => {
    setMyVotes((prev) => {
      const filtered = prev.filter((v) => v.stall_id !== vote.stall_id);
      return [...filtered, vote];
    });
  };

  const handleStallUpdate = (updatedStall) => {
    setBoard((prev) => ({
      ...prev,
      departments: prev.departments.map((dept) => ({
        ...dept,
        stalls: dept.stalls.map((s) => (s.id === updatedStall.id ? updatedStall : s)),
      })),
    }));
  };

  const exportBoard = async () => {
    try {
      const res = await boardAPI.exportBoard(boardId, "json");
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${board.name}-export.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${res.data.count} use cases`);
    } catch {
      toast.error("Export failed");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="animate-pulse font-heading text-lg text-slate-500">Loading board...</div>
      </div>
    );
  }

  if (!board) return null;

  // Show setup wizard for new boards (owner only, setup_step explicitly 0-2)
  const setupStep = board.setup_step;
  const showSetup = isOwner && setupStep !== undefined && setupStep !== null && setupStep < 4;

  if (showSetup) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]" data-testid="game-board-setup">
        <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200">
          <div className="px-4 sm:px-6 h-14 flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} className="h-8 px-2 text-slate-500">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="font-heading font-bold text-base text-slate-900">{board.name}</h1>
              <span className="text-xs text-slate-400">Game Setup</span>
            </div>
          </div>
        </header>
        <div className="overflow-y-auto pb-8">
          <SetupWizard
            board={board}
            onComplete={() => { fetchBoard(); }}
            onRefresh={fetchBoard}
          />
        </div>
      </div>
    );
  }

  const applicableDepts = board.departments.filter((d) => d.is_applicable);
  const totalStalls = applicableDepts.reduce((sum, d) => sum + d.stalls.length, 0);

  // Current user's assigned departments
  const myParticipant = board.participants?.find((p) => p.user_id === user?.id);
  const myAssignedDepts = new Set(myParticipant?.assigned_departments || []);
  const hasAssignments = myAssignedDepts.size > 0;
  const decidedStalls = applicableDepts.reduce(
    (sum, d) => sum + d.stalls.filter((s) => s.decision).length, 0
  );
  const progress = totalStalls > 0 ? Math.round((decidedStalls / totalStalls) * 100) : 0;

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col" data-testid="game-board-page">
      {/* Countdown Banner */}
      {(board.scheduled_end || board.scheduled_start) && (
        <CountdownBanner
          scheduledEnd={board.scheduled_end || board.scheduled_start}
          durationMinutes={board.duration_minutes}
          gameMode={board.game_mode}
        />
      )}

      {/* Voting Mode Banner */}
      {board.voting_enabled && !board.votes_revealed && (
        <div className="bg-indigo-50 border-b border-indigo-200 px-4 py-2 flex items-center justify-center gap-3" data-testid="voting-mode-banner">
          <Vote className="w-4 h-4 text-indigo-500" />
          <span className="text-sm font-medium text-indigo-700">
            Blind Voting Mode — Your votes are private until revealed
          </span>
          <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200 text-[10px]">
            {board.voting_mode === "assigned" ? "Assigned Departments" : "Full Board"}
          </Badge>
        </div>
      )}

      {board.votes_revealed && (
        <div className="bg-emerald-50 border-b border-emerald-200 px-4 py-2 flex items-center justify-center gap-3" data-testid="votes-revealed-banner">
          <Eye className="w-4 h-4 text-emerald-500" />
          <span className="text-sm font-medium text-emerald-700">
            Votes Revealed — Majority decisions applied
          </span>
          <Button
            variant="ghost"
            size="sm"
            data-testid="view-results-banner-btn"
            onClick={() => setShowVoteResults(true)}
            className="h-6 text-xs text-emerald-700 hover:text-emerald-800 hover:bg-emerald-100"
          >
            View Results
          </Button>
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200">
        <div className="px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              data-testid="back-to-dashboard-btn"
              onClick={() => navigate("/dashboard")}
              className="h-8 px-2 text-slate-500"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-heading font-bold text-base text-slate-900 leading-tight">{board.name}</h1>
                {board.game_mode && (
                  <Badge variant="outline" className="text-[10px] text-slate-400 border-slate-200">
                    {board.game_mode === "live" ? "Live" : "Async"}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <span>{decidedStalls}/{totalStalls} decided</span>
                <span className="text-slate-300">|</span>
                <span>{progress}% complete</span>
                {board.participants && board.participants.length > 1 && (
                  <>
                    <span className="text-slate-300">|</span>
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {board.participants.length} players
                    </span>
                  </>
                )}
              </div>
            </div>
            <PresenceBar onlineUsers={onlineUsers} connected={connected} currentUserId={user?.id} />
          </div>

          <div className="flex items-center gap-1.5">
            {timerActive && (
              <div className="flex items-center gap-2 bg-slate-100 rounded-lg px-3 py-1.5 mr-1">
                <Timer className="w-3.5 h-3.5 text-slate-500" />
                <span className={`font-mono text-sm font-medium ${timerSeconds < 60 ? "text-red-600" : "text-slate-700"}`}>
                  {formatTime(timerSeconds)}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => {
                    const newRunning = !timerRunning;
                    setTimerRunning(newRunning);
                    sendTimerSync(newRunning ? "started" : "paused", timerSeconds);
                  }}
                >
                  {timerRunning ? "Pause" : "Start"}
                </Button>
              </div>
            )}

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid="tutorial-btn"
                    onClick={() => setShowTutorials(true)}
                    className="h-8 w-8 p-0 text-slate-400 hover:text-indigo-600"
                  >
                    <BookOpen className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Tutorial Videos</TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid="activity-feed-btn"
                    onClick={() => setShowActivity(true)}
                    className="h-8 w-8 p-0 text-slate-400 hover:text-indigo-600 relative"
                  >
                    <Activity className="w-4 h-4" />
                    {activities.length > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-indigo-500 rounded-full" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Live Activity Feed</TooltipContent>
              </Tooltip>

              {isOwner && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid="invite-btn"
                      onClick={() => setShowInvite(true)}
                      className="h-8 w-8 p-0 text-slate-400 hover:text-indigo-600"
                    >
                      <UserPlus className="w-4 h-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Invite Participants</TooltipContent>
                </Tooltip>
              )}

              {isOwner && (
                <Button
                  size="sm"
                  data-testid="toggle-game-btn"
                  onClick={toggleGameStatus}
                  className={`h-8 text-xs font-medium px-3 ${
                    board.status === "completed"
                      ? "bg-indigo-600 hover:bg-indigo-700 text-white"
                      : "bg-red-600 hover:bg-red-700 text-white"
                  }`}
                >
                  {board.status === "completed" ? (
                    <><Play className="w-3.5 h-3.5 mr-1.5" />Continue Game</>
                  ) : (
                    <><Flag className="w-3.5 h-3.5 mr-1.5" />End Game</>
                  )}
                </Button>
              )}

              {isOwner && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid="voting-settings-btn"
                      onClick={() => setShowVotingSettings(true)}
                      className={`h-8 w-8 p-0 ${board.voting_enabled ? "text-indigo-500" : "text-slate-400 hover:text-slate-600"}`}
                    >
                      <Vote className="w-4 h-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Voting Settings</TooltipContent>
                </Tooltip>
              )}

              {isOwner && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid="dept-assign-btn"
                      onClick={() => setShowDeptAssign(true)}
                      className="h-8 w-8 p-0 text-slate-400 hover:text-indigo-600"
                    >
                      <UserCheck className="w-4 h-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Assign Departments</TooltipContent>
                </Tooltip>
              )}

              {board.votes_revealed && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid="view-vote-results-btn"
                      onClick={() => setShowVoteResults(true)}
                      className="h-8 w-8 p-0 text-indigo-500 hover:text-indigo-600"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>View Vote Results</TooltipContent>
                </Tooltip>
              )}

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid="timer-toggle-btn"
                    onClick={() => {
                      const newActive = !timerActive;
                      setTimerActive(newActive);
                      const secs = board.duration_minutes ? board.duration_minutes * 60 : 300;
                      setTimerSeconds(secs);
                      setTimerRunning(false);
                      sendTimerSync(newActive ? "started" : "reset", secs);
                    }}
                    className="h-8 w-8 p-0 text-slate-400 hover:text-slate-600"
                  >
                    <Timer className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Toggle Timer</TooltipContent>
              </Tooltip>

              {/* Spotlight indicator/clear */}
              {spotlight && (
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid="clear-spotlight-btn"
                  onClick={() => { setSpotlight(null); sendSpotlightClear(); }}
                  className="h-8 px-2 text-amber-500 hover:text-amber-600 bg-amber-50 hover:bg-amber-100 text-xs"
                >
                  <Crosshair className="w-3.5 h-3.5 mr-1 animate-pulse" />
                  Clear Spotlight
                </Button>
              )}

              {isOwner && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid="lock-toggle-btn"
                      onClick={toggleLock}
                      className={`h-8 w-8 p-0 ${board.is_locked ? "text-red-500" : "text-slate-400 hover:text-slate-600"}`}
                    >
                      {board.is_locked ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{board.is_locked ? "Unlock Board" : "Lock Board"}</TooltipContent>
                </Tooltip>
              )}

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid="analytics-btn"
                    onClick={() => navigate(`/board/${boardId}/analytics`)}
                    className="h-8 w-8 p-0 text-slate-400 hover:text-slate-600"
                  >
                    <BarChart3 className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>View Analytics</TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid="export-btn"
                    onClick={exportBoard}
                    className="h-8 w-8 p-0 text-slate-400 hover:text-slate-600"
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Export Use Cases</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
        <Progress value={progress} className="h-1 rounded-none" />
      </header>

      {/* Part 1 / Part 2 Tab Bar */}
      <div className="bg-white border-b border-slate-200">
        <div className="grid grid-cols-2">
          <button
            data-testid="tab-optimization"
            onClick={() => setActiveTab("optimization")}
            className={`flex items-center justify-center gap-2.5 py-3.5 text-sm font-semibold border-b-3 transition-colors ${
              activeTab === "optimization"
                ? "border-b-[3px] border-indigo-500 text-indigo-700 bg-indigo-50/50"
                : "border-b-[3px] border-transparent text-slate-400 hover:text-slate-600 hover:bg-slate-50"
            }`}
          >
            <Anchor className="w-5 h-5" />
            <span>Part 1: Deep Optimization</span>
          </button>
          <button
            data-testid="tab-disruption"
            onClick={() => setActiveTab("disruption")}
            className={`flex items-center justify-center gap-2.5 py-3.5 text-sm font-semibold border-b-3 transition-colors ${
              activeTab === "disruption"
                ? "border-b-[3px] border-red-500 text-red-700 bg-red-50/50"
                : "border-b-[3px] border-transparent text-slate-400 hover:text-slate-600 hover:bg-slate-50"
            }`}
          >
            <Skull className="w-5 h-5" />
            <span>Part 2: Radical Disruption</span>
          </button>
        </div>
      </div>

      {activeTab === "disruption" ? (
        <div className="flex-1 overflow-hidden">
          <DisruptionSession boardId={boardId} board={board} />
        </div>
      ) : (
      <>
      {/* Game Complete — Go to Part 2 CTA */}
      {board.status === "completed" && activeTab === "optimization" && (
        <div className="bg-emerald-600 px-4 py-3 flex items-center justify-center gap-4" data-testid="part2-cta-banner">
          <CheckCircle2 className="w-5 h-5 text-white" />
          <span className="text-sm font-medium text-white">Part 1 Complete! Ready for Radical Disruption?</span>
          <Button
            data-testid="go-to-part2-btn"
            onClick={() => setActiveTab("disruption")}
            size="sm"
            className="bg-white text-emerald-700 hover:bg-emerald-50 h-8 text-xs font-semibold"
          >
            <Skull className="w-3.5 h-3.5 mr-1.5" />
            Go to Part 2
          </Button>
        </div>
      )}

      {/* Async mode — Player can go to Part 2 anytime */}
      {board.status !== "completed" && board.game_mode === "async" && activeTab === "optimization" && (
        <div className="bg-indigo-50 border-b border-indigo-200 px-4 py-2 flex items-center justify-center gap-3" data-testid="async-part2-option">
          <span className="text-xs text-indigo-600">Finished your departments?</span>
          <Button
            data-testid="async-go-to-part2-btn"
            onClick={() => setActiveTab("disruption")}
            size="sm"
            variant="outline"
            className="h-6 text-[10px] border-indigo-300 text-indigo-700 hover:bg-indigo-100"
          >
            <Skull className="w-3 h-3 mr-1" />
            Continue to Part 2
          </Button>
        </div>
      )}

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <aside className="w-60 bg-white border-r border-slate-200 hidden md:flex flex-col" data-testid="department-sidebar">
          <div className="px-4 py-3 border-b border-slate-100">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Departments</span>
          </div>
          <ScrollArea className="flex-1">
            <div className="py-1">
              {board.departments.map((dept, idx) => {
                const Icon = DEPT_ICONS[dept.icon] || Settings;
                const deptDecided = dept.stalls.filter((s) => s.decision).length;
                const isActive = activeDeptIdx === idx;
                const isMyDept = hasAssignments && myAssignedDepts.has(dept.id);
                return (
                  <div
                    key={dept.id}
                    data-testid={`dept-sidebar-${idx}`}
                    className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer group transition-colors ${
                      isActive ? "bg-indigo-50 border-r-2 border-indigo-500" : "hover:bg-slate-50"
                    } ${!dept.is_applicable ? "opacity-40" : ""}`}
                    onClick={() => {
                      setActiveDeptIdx(idx);
                      sendViewing(dept.name);
                      const el = document.getElementById(`dept-col-${idx}`);
                      el?.scrollIntoView({ behavior: "smooth", inline: "start" });
                    }}
                  >
                    <div className="relative">
                      <Icon className={`w-4 h-4 shrink-0 ${isActive ? "text-indigo-600" : isMyDept ? "text-indigo-500" : "text-slate-400"}`} />
                      {isMyDept && (
                        <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-indigo-500 ring-1 ring-white" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className={`text-sm font-medium truncate ${isActive ? "text-indigo-700" : isMyDept ? "text-slate-800" : "text-slate-700"}`}>
                          {dept.name}
                        </span>
                        {isMyDept && (
                          <span className="text-[8px] font-bold uppercase tracking-wider text-indigo-500 bg-indigo-50 px-1 py-0.5 rounded shrink-0">You</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className={`text-[10px] ${deptDecided === dept.stalls.length && dept.is_applicable ? "text-emerald-600 font-medium" : "text-slate-400"}`}>
                          {deptDecided === dept.stalls.length && dept.is_applicable ? "Done" : `${deptDecided}/${dept.stalls.length}`}
                        </span>
                        {/* Tiny dots for users viewing this dept */}
                        {onlineUsers.filter((u) => u.viewing_department === dept.name && u.user_id !== user?.id).map((u) => (
                          <span key={u.user_id} className="w-1.5 h-1.5 rounded-full bg-emerald-400" title={u.name} />
                        ))}
                      </div>
                    </div>
                    {isOwner && (
                      <Switch
                        checked={dept.is_applicable}
                        onCheckedChange={() => toggleDept(dept.id, dept.is_applicable)}
                        className="scale-75"
                        data-testid={`dept-toggle-${idx}`}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </aside>

        {/* Board */}
        <main className="flex-1 overflow-x-auto overflow-y-auto scrollbar-thin">
          <div className="flex gap-5 p-5 min-h-full" style={{ minWidth: applicableDepts.length * 340 }}>
            {board.departments.map((dept, deptIdx) => {
              if (!dept.is_applicable) return null;
              const Icon = DEPT_ICONS[dept.icon] || Settings;
              const deptDecided = dept.stalls.filter((s) => s.decision).length;
              const deptTotal = dept.stalls.length;
              const deptProgress = Math.round((deptDecided / deptTotal) * 100);
              const isMyDept = hasAssignments && myAssignedDepts.has(dept.id);
              const isSpotlighted = spotlight && (
                (spotlight.target_type === "department" && spotlight.department_idx === deptIdx) ||
                (spotlight.target_type === "stall" && dept.stalls?.some((s) => s.id === spotlight.stall_id))
              );
              const isDimmed = spotlight && !isSpotlighted;

              return (
                <div
                  key={dept.id}
                  id={`dept-col-${deptIdx}`}
                  className={`w-[320px] shrink-0 flex flex-col transition-opacity duration-300 ${isDimmed ? "opacity-20 pointer-events-none" : ""}`}
                  data-testid={`dept-column-${deptIdx}`}
                >
                  <div className={`bg-white rounded-xl border shadow-sm p-4 mb-3 ${
                    deptProgress === 100 ? "border-emerald-300 bg-emerald-50/30" :
                    isMyDept ? "border-indigo-300 bg-indigo-50/20 ring-2 ring-indigo-200 ring-offset-1" :
                    "border-slate-200"
                  }`}>
                    <div className="flex items-center gap-2.5 mb-2">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        deptProgress === 100 ? "bg-emerald-100" :
                        isMyDept ? "bg-indigo-100" :
                        "bg-slate-100"
                      }`}>
                        {deptProgress === 100 ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                        ) : (
                          <Icon className={`w-4 h-4 ${isMyDept ? "text-indigo-600" : "text-slate-600"}`} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <h3 className="font-heading font-semibold text-sm text-slate-900">{dept.name}</h3>
                          {isMyDept && (
                            <Badge className="bg-indigo-100 text-indigo-600 border-indigo-200 text-[8px] px-1.5 py-0">
                              Assigned to you
                            </Badge>
                          )}
                        </div>
                        <span className={`text-[10px] ${deptProgress === 100 ? "text-emerald-600 font-medium" : "text-slate-400"}`}>
                          {deptProgress === 100 ? "Complete" : `${deptDecided}/${deptTotal} decided`}
                        </span>
                      </div>
                    </div>
                    <Progress value={deptProgress} className="h-1.5" />
                    {/* Spotlight button — facilitator only */}
                    {isOwner && (
                      <div className="flex items-center justify-end mt-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`spotlight-dept-${deptIdx}`}
                          onClick={() => {
                            sendSpotlight("department", deptIdx, dept.name, null, null);
                            setSpotlight({ target_type: "department", department_idx: deptIdx, department_name: dept.name });
                          }}
                          className={`h-6 text-[9px] px-2 ${isSpotlighted ? "text-amber-600 bg-amber-50" : "text-slate-400 hover:text-amber-600"}`}
                        >
                          <Crosshair className="w-3 h-3 mr-0.5" />
                          {isSpotlighted ? "Spotlighted" : "Spotlight"}
                        </Button>
                      </div>
                    )}
                    {/* Online users viewing this department */}
                    {onlineUsers.filter((u) => u.viewing_department === dept.name && u.user_id !== user?.id).length > 0 && (
                      <div className="flex items-center gap-1 mt-2.5 pt-2 border-t border-slate-100">
                        <span className="text-[9px] text-slate-400 mr-1">Viewing:</span>
                        {onlineUsers.filter((u) => u.viewing_department === dept.name && u.user_id !== user?.id).slice(0, 4).map((u) => (
                          <TooltipProvider key={u.user_id}>
                            <Tooltip>
                              <TooltipTrigger>
                                <div className="w-5 h-5 rounded-full bg-indigo-500 flex items-center justify-center text-[8px] font-bold text-white ring-1 ring-white">
                                  {(u.name || "?")[0].toUpperCase()}
                                </div>
                              </TooltipTrigger>
                              <TooltipContent><span className="text-xs">{u.name}</span></TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ))}
                      </div>
                    )}
                    {/* Assigned users */}
                    {(() => {
                      const assignedUsers = (board.participants || []).filter((p) =>
                        p.assigned_departments && p.assigned_departments.includes(dept.id)
                      );
                      if (assignedUsers.length === 0) return null;
                      return (
                        <div className="flex items-center gap-1 mt-2 pt-2 border-t border-slate-100">
                          <UserCheck className="w-3 h-3 text-slate-300 mr-0.5" />
                          <div className="flex items-center -space-x-1">
                            {assignedUsers.slice(0, 5).map((p) => (
                              <TooltipProvider key={p.user_id}>
                                <Tooltip>
                                  <TooltipTrigger>
                                    <div className="w-5 h-5 rounded-full bg-slate-400 flex items-center justify-center text-[8px] font-bold text-white ring-1 ring-white">
                                      {(p.name || "?")[0].toUpperCase()}
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent><span className="text-xs">{p.name} (assigned)</span></TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ))}
                          </div>
                          <span className="text-[9px] text-slate-400 ml-1">assigned</span>
                        </div>
                      );
                    })()}
                  </div>

                  <div className="space-y-2 flex-1">
                    {dept.stalls.map((stall, stallIdx) => {
                      const decisionStyle = stall.decision ? DECISION_COLORS[stall.decision] : null;
                      const isStallSpotlighted = spotlight?.target_type === "stall" && spotlight?.stall_id === stall.id;
                      return (
                        <div
                          key={stall.id}
                          data-testid={`stall-card-${deptIdx}-${stallIdx}`}
                          onClick={() => {
                            handleStallClick(stall, dept.name);
                          }}
                          className={`bg-white rounded-xl border p-3.5 cursor-pointer hover:shadow-md hover:border-slate-300 group animate-fade-in ${
                            stall.decision ? `border-l-4 ${decisionStyle?.border}` : "border-l-4 border-l-slate-200"
                          } ${isStallSpotlighted ? "ring-2 ring-amber-400 ring-offset-1 border-amber-300 shadow-lg" : "border-slate-200"}`}
                          style={{ animationDelay: `${stallIdx * 40}ms` }}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <h4 className="font-heading font-medium text-sm text-slate-800 leading-snug group-hover:text-indigo-600">
                                {stall.name}
                              </h4>
                              {stall.is_custom && (
                                <Badge className="bg-indigo-50 text-indigo-500 border-indigo-200 text-[8px] mt-0.5">
                                  <PenLine className="w-2 h-2 mr-0.5" />Custom
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-1 shrink-0 mt-0.5">
                              {isOwner && (
                                <button
                                  data-testid={`spotlight-stall-${deptIdx}-${stallIdx}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    sendSpotlight("stall", deptIdx, dept.name, stall.id, stall.name);
                                    setSpotlight({ target_type: "stall", department_idx: deptIdx, stall_id: stall.id, department_name: dept.name, stall_name: stall.name });
                                  }}
                                  className={`w-5 h-5 rounded flex items-center justify-center opacity-0 group-hover:opacity-60 hover:!opacity-100 transition-opacity ${
                                    isStallSpotlighted ? "!opacity-100 text-amber-500 bg-amber-50" : "text-slate-400"
                                  }`}
                                >
                                  <Crosshair className="w-3 h-3" />
                                </button>
                              )}
                              <ChevronRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-indigo-400" />
                            </div>
                          </div>
                          <p className="text-xs text-slate-400 mt-1 line-clamp-2 leading-relaxed">
                            {stall.description}
                          </p>
                          <div className="flex items-center gap-2 mt-2.5">
                            {stall.decision ? (
                              <Badge className={`text-[10px] border ${decisionStyle?.badge}`}>
                                {stall.decision === "delete" && <Trash className="w-2.5 h-2.5 mr-1" />}
                                {stall.decision === "automate" && <Zap className="w-2.5 h-2.5 mr-1" />}
                                {stall.decision === "augment" && <Brain className="w-2.5 h-2.5 mr-1" />}
                                {stall.decision === "protect" && <Shield className="w-2.5 h-2.5 mr-1" />}
                                {stall.decision.charAt(0).toUpperCase() + stall.decision.slice(1)}
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-[10px] text-slate-400 border-slate-200">
                                Undecided
                              </Badge>
                            )}
                            {stall.confidence > 0 && (
                              <span className="text-[10px] text-amber-500 flex items-center gap-0.5">
                                <Star className="w-2.5 h-2.5 fill-amber-400 text-amber-400" />
                                {stall.confidence}
                              </span>
                            )}
                            {board.voting_enabled && (
                              <>
                                {myVotes.find((v) => v.stall_id === stall.id) && (
                                  <Badge className="bg-indigo-50 text-indigo-600 border-indigo-200 text-[10px]">
                                    <Vote className="w-2.5 h-2.5 mr-0.5" />
                                    Voted
                                  </Badge>
                                )}
                                {voteCounts[stall.id] > 0 && (
                                  <span className="text-[10px] text-slate-400">{voteCounts[stall.id]} votes</span>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}

                    {/* Add Custom Idea */}
                    <button
                      data-testid={`add-stall-${deptIdx}`}
                      onClick={() => setAddStallDept(dept.id)}
                      className="w-full border-2 border-dashed border-slate-200 rounded-xl p-3 flex items-center justify-center gap-2 hover:border-indigo-300 hover:bg-indigo-50/50 transition-colors group/add mt-1"
                    >
                      <PlusIcon className="w-4 h-4 text-slate-300 group-hover/add:text-indigo-500" />
                      <span className="text-xs text-slate-300 group-hover/add:text-indigo-600">Add Custom Idea</span>
                    </button>
                  </div>
                </div>
              );
            })}

            {/* Inline Invite Button on the Board */}
            {isOwner && (
              <div className="w-[200px] shrink-0 flex flex-col items-center justify-start pt-4">
                <button
                  data-testid="board-inline-invite-btn"
                  onClick={() => setShowInvite(true)}
                  className="w-full border-2 border-dashed border-slate-300 rounded-xl p-6 flex flex-col items-center gap-2 hover:border-indigo-400 hover:bg-indigo-50/50 transition-colors group"
                >
                  <UserPlus className="w-6 h-6 text-slate-400 group-hover:text-indigo-500" />
                  <span className="text-xs font-medium text-slate-400 group-hover:text-indigo-600">Invite Players</span>
                </button>
              </div>
            )}
          </div>
        </main>
      </div>
      </>
      )}

      {/* Decision or Voting Panel */}
      {selectedStall && board.voting_enabled && !board.votes_revealed ? (
        <VotingPanel
          stall={selectedStall}
          departmentName={selectedDeptName}
          boardId={boardId}
          myVotes={myVotes}
          isLocked={board.is_locked}
          onVoted={handleVoted}
          onClose={() => setSelectedStall(null)}
        />
      ) : selectedStall ? (
        <DecisionPanel
          stall={selectedStall}
          departmentName={selectedDeptName}
          boardId={boardId}
          isLocked={board.is_locked}
          industry={board.industry}
          companySize={board.company_size}
          onUpdate={handleStallUpdate}
          onClose={() => setSelectedStall(null)}
        />
      ) : null}

      {/* Invite Modal */}
      <InviteModal
        open={showInvite}
        onOpenChange={setShowInvite}
        boardId={boardId}
        boardName={board.name}
        gameMode={board.game_mode}
        scheduledStart={board.scheduled_start}
        scheduledEnd={board.scheduled_end}
        durationMinutes={board.duration_minutes}
      />

      {/* Voting Settings */}
      <VotingSettings
        open={showVotingSettings}
        onOpenChange={setShowVotingSettings}
        board={board}
        onUpdate={fetchBoard}
      />

      {/* Department Assignment */}
      <DepartmentAssignment
        open={showDeptAssign}
        onOpenChange={setShowDeptAssign}
        board={board}
        onUpdate={fetchBoard}
      />

      {/* Vote Results */}
      <VoteRevealView
        open={showVoteResults}
        onOpenChange={setShowVoteResults}
        boardId={boardId}
        isOwner={isOwner}
        onBoardRefresh={fetchBoard}
      />

      {/* Tutorial Videos */}
      <TutorialVideos
        open={showTutorials}
        onOpenChange={setShowTutorials}
        boardId={boardId}
      />

      {/* Activity Feed */}
      <ActivityFeed
        open={showActivity}
        onOpenChange={setShowActivity}
        activities={activities}
      />

      {/* Add Custom Stall Dialog */}
      <Dialog open={!!addStallDept} onOpenChange={() => setAddStallDept(null)}>
        <DialogContent className="sm:max-w-md" data-testid="add-stall-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg flex items-center gap-2">
              <PlusIcon className="w-5 h-5 text-indigo-500" />
              Add Custom Idea
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">Add a custom workflow or idea specific to your business.</p>
          <div className="space-y-4 py-3">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Name *</label>
              <Input
                data-testid="custom-stall-name"
                placeholder="e.g., Partner Relationship Management"
                value={newStall.name}
                onChange={(e) => setNewStall({ ...newStall, name: e.target.value })}
                className="h-10 bg-white"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Description</label>
              <Textarea
                data-testid="custom-stall-desc"
                placeholder="Brief description of this workflow area..."
                value={newStall.description}
                onChange={(e) => setNewStall({ ...newStall, description: e.target.value })}
                className="min-h-[80px] bg-white text-sm resize-none"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddStallDept(null)}>Cancel</Button>
            <Button
              data-testid="submit-custom-stall-btn"
              onClick={addCustomStall}
              disabled={!newStall.name.trim()}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              <PlusIcon className="w-4 h-4 mr-1.5" />Add Idea
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
